'use client';

import { Box, Button, Container, IconButton, Paper, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { AlertCircleIcon, SearchIcon, XIcon } from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { AgentCard } from 'src/components/agent/agent-card';
import { CityAgentCard } from 'src/components/agent/city-agent-card';
import type { Agent } from 'src/data/models/agent.model';
import type { Destination } from 'src/data/models/destination.model';
import { agentRemotedata } from 'src/data/network/remote-data/agent-remote-data';
import { masterRemotedata } from 'src/data/network/remote-data/master-remote-data';
import { themeConfig } from 'src/theme';
import { PROVINCES } from 'src/utils/consts';

export default function View() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [agents, setAgents] = useState<Agent[]>([]);
  const [destinations, setDestinations] = useState<Destination[]>([]);

  const updateRoute = (props: { search?: string }) => {
    router.replace(`/agen?search=${props.search ?? search}`);
  };

  const getAgents = async () => {
    setLoading(true);
    const data = await agentRemotedata.getAgents({
      search,
    });
    setLoading(false);
    setAgents(data.items);
  };

  const getDestinations = async () => {
    setLoading(true);
    const data = await masterRemotedata.getDestinationsGroups({
      province: search,
    });
    setLoading(false);
    setDestinations(data);
  };

  useEffect(() => {
    if (search) {
      if (search.toLocaleLowerCase() === 'lampung') {
        getAgents();
        setDestinations([]);
      } else {
        getDestinations();
        setAgents([]);
      }
    } else {
      setAgents([]);
    }
  }, [searchParams.get('search')]);

  return (
    <Box>
      <Box
        sx={{
          paddingBottom: '16px',
          borderRadius: '0px 0px 50px 50px',
          borderBottom: '5px solid var(--secondary-main, #FEFE01)',
          background:
            'var(--gradient-primary-bold, linear-gradient(135deg, var(--primary-main, #11BB18) 0%, var(--primary-dark, #007867) 100%))',
          boxShadow:
            'var(--color-x, 0px) var(--color-y, 8px) var(--color-blur, 16px) var(--color-spread, 0px) var(--primary-24, rgba(0, 167, 111, 0.24))',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            borderRadius: '0px 0px 50px 50px',
            backgroundImage: {
              xs: 'url(/assets/images/image-bus-5.webp)',
              md: 'url(/assets/images/image-bus-4.webp)',
            },
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
        >
          <Box
            sx={{
              borderRadius: '0px 0px 50px 50px',
              background: 'linear-gradient(0deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.85) 100%)',
            }}
          >
            <Container>
              <div className="flex flex-col justify-between min-h-[60vh] md:min-h-[90vh] py-[40px] md:py-[100px]">
                <Typography variant="h1" color="secondary">
                  Jaringan Agen
                </Typography>

                <div className="flex flex-col items-center gap-[20px] md:gap-[30px]">
                  <Typography variant="h1" color="white">
                    Pencarian Agen
                  </Typography>
                  <Paper
                    className="p-[10px] md:p-[20px]"
                    sx={{
                      borderRadius: '50px',
                      width: '80%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <TextField
                      size="small"
                      id="outlined-basic"
                      variant="outlined"
                      className="grow"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Cari kota/agen terdekat"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          updateRoute({});
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'transparent',
                          },
                          '&:hover fieldset': {
                            borderColor: 'transparent',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'transparent',
                            color: 'white',
                            '&::placeholder': {
                              color: 'white',
                              fontSize: '18px',
                              fontWeight: 600,
                            },
                          },
                        },
                        '& .MuiOutlinedInput-input': {
                          backgroundColor: 'transparent',
                        },
                      }}
                    />
                    {search && (
                      <IconButton
                        size="large"
                        color="primary"
                        onClick={() => {
                          router.replace(`/agen`);
                          setSearch('');
                        }}
                        aria-label="Cari Agen Puspa Jaya"
                      >
                        <XIcon color="black" />
                      </IconButton>
                    )}
                    <div className="block md:hidden">
                      <IconButton
                        size="large"
                        color="primary"
                        onClick={() => updateRoute({})}
                        aria-label="Cari Agen Puspa Jaya"
                        sx={{
                          background: themeConfig.gradient.primary.bold,
                        }}
                      >
                        <SearchIcon color="white" />
                      </IconButton>
                    </div>
                    <div className="hidden md:block">
                      <Button
                        startIcon={<SearchIcon />}
                        variant="contained"
                        size="large"
                        color="primary"
                        onClick={() => updateRoute({})}
                        sx={{
                          background: themeConfig.gradient.primary.bold,
                        }}
                      >
                        Cari Agent
                      </Button>
                    </div>
                  </Paper>
                </div>
              </div>
            </Container>
          </Box>
        </Box>
      </Box>

      <Container className="py-[40px] md:py-[100px]">
        {!search && (
          <Grid container spacing={{ xs: '16px', md: '28px' }}>
            {PROVINCES.map((e, i) => (
              <Grid size={{ xs: 6, md: 3 }} key={i}>
                <CityAgentCard
                  onClick={() => {
                    setSearch(e.value);
                    updateRoute({ search: e.value });
                  }}
                  image_asset={e.image_asset}
                  name={e.name}
                />
              </Grid>
            ))}
          </Grid>
        )}
        {search && (
          <div className="flex flex-col gap-[100px]">
            {search && agents.length > 0 && (
              <Typography variant="h5">
                Menampilkan pencarian “{search}”, hasil pencarian {agents.length} agen ditemukan.
              </Typography>
            )}
            {search && destinations?.length > 0 && (
              <Typography variant="h5">
                Menampilkan pencarian “{search}”, hasil pencarian{' '}
                {destinations[0].addresses?.length} agen ditemukan.
              </Typography>
            )}
            <Grid container spacing={{ xs: '16px', md: '28px' }}>
              {agents.map((e, i) => (
                <Grid size={{ xs: 12, md: 6 }} key={i}>
                  <AgentCard agent={e} />
                </Grid>
              ))}
              {destinations.map((e, i) =>
                e.addresses?.map((address, j) => (
                  <Grid size={{ xs: 12, md: 6 }} key={i}>
                    <AgentCard address={address} />
                  </Grid>
                ))
              )}
              {agents.length === 0 && destinations.length === 0 && (
                <div className="flex flex-col justify-center items-center w-full">
                  <AlertCircleIcon />
                  <Typography variant="h3">Mohon maaf, agen yang anda cari tidak ada.</Typography>
                </div>
              )}
            </Grid>
          </div>
        )}
      </Container>
    </Box>
  );
}
